import { MIDIValInput } from '@midival/core';
import {
  actionTypes,
  FetchClientDataRequestPayload,
  FetchClientDataSuccessPayload,
  FetchClientDataErrorPayload,
  SetLevelSelectPayload,
  SetUnitSelectPayload,
  FetchTutorialDataSuccessPayload,
  UserLevelDataSuccessPayload,
  SetHighestLevelCompletedPayload,
  LevelDataSuccessPayload,
  TiersByLevels,
  IUserData,
  AppState,
  ErrorRecognitionActive,
  SetRepDownbeatsSound,
  SetRepMetronomeSound,
  SetSightReadingCursorVisible,
} from '../Types';
import { COLORBLIND_TYPE } from 'Utils/Constants';

export const fetchClientDataRequest = (payload: FetchClientDataRequestPayload) => ({
  type: actionTypes.FETCH_CLIENT_DATA_REQUEST,
  payload,
});
export const fetchClientDataSuccess = (payload: FetchClientDataSuccessPayload) => ({
  type: actionTypes.FETCH_CLIENT_DATA_SUCCESS,
  payload,
});
export const fetchClientDataError = (payload: FetchClientDataErrorPayload) => ({
  type: actionTypes.FETCH_CLIENT_DATA_ERROR,
  payload,
});

export const setPlayOnStart = (payload: boolean) => ({
  type: actionTypes.SET_PLAY_ON_START,
  payload,
});

export const setLevelSelect = (payload: SetLevelSelectPayload) => ({
  type: actionTypes.SET_LEVEL_SELECT,
  payload,
});
export const setUnitSelect = (payload: SetUnitSelectPayload) => ({
  type: actionTypes.SET_UNIT_SELECT,
  payload,
});
export const setTempo = (payload: { tempo: number; level: number; tier: number }) => ({
  type: actionTypes.SET_TEMPO,
  payload,
});

export const setHighestLevelCompleted = (payload: SetHighestLevelCompletedPayload) => ({
  type: actionTypes.SET_HIGHEST_LEVEL_COMPLETED,
  payload,
});

export const fetchTutorialData = () => ({
  type: actionTypes.FETCH_TUTORIAL_DATA,
});

export const fetchTutorialDataProcessing = (payload: boolean) => ({
  type: actionTypes.FETCH_TUTORIAL_DATA_PROCESSING,
  payload,
});

export const fetchTutorialDataSuccess = (payload: FetchTutorialDataSuccessPayload) => ({
  type: actionTypes.FETCH_TUTORIAL_DATA_SUCCESS,
  payload,
});

export const midiInputSetUp = (payload: MIDIValInput) => ({
  type: actionTypes.MIDI_INPUT_SETUP,
  payload,
});

export const authenticatedStartup = () => ({
  type: actionTypes.AUTHENTICATED_STARTUP,
});

export const fetchUserLevelsSuccess = (payload: UserLevelDataSuccessPayload) => ({
  type: actionTypes.FETCH_USER_LEVEL_SUCCESS,
  payload,
});

export const fetchLevelDataSuccess = (payload: LevelDataSuccessPayload) => ({
  type: actionTypes.FETCH_LEVEL_DATA_SUCCESS,
  payload,
});

export const updateTiersByLevels = (payload: TiersByLevels[]) => ({
  type: actionTypes.UPDATE_TIERS_BY_LEVELS,
  payload,
});

export const setLastShowSurvey = (payload: { last_shown_survey: Date }) => ({
  type: actionTypes.SET_LAST_SHOWN_SURVEY,
  payload,
});

export const setShownIntroduction = (payload: boolean) => ({
  type: actionTypes.SET_SHOWN_INTRODUCTION,
  payload,
});

export const setUserData = (payload: Partial<IUserData>) => ({
  type: actionTypes.SET_USER_DATA,
  payload,
});

export const updateUserData = (payload: Partial<IUserData>) => ({
  type: actionTypes.UPDATE_USER_DATA,
  payload,
});

export const setAudioOn = (payload: { audioOn: boolean }) => ({
  type: actionTypes.SET_AUDIO_ON,
  payload: payload,
});

export const setColorblindType = (coloblindType: COLORBLIND_TYPE | null) => ({
  type: actionTypes.SET_COLORBLIND_TYPE,
  payload: coloblindType,
});

export const setRepMetronomeSound = (payload: boolean): SetRepMetronomeSound => ({
  type: actionTypes.SET_REP_METRONOME_SOUND,
  payload: payload,
});

export const setRepDownbeatsSound = (payload: boolean): SetRepDownbeatsSound => ({
  type: actionTypes.SET_REP_DOWNBEATS_SOUND,
  payload: payload,
});

export const setSightReadingCursorVisible = (payload: boolean): SetSightReadingCursorVisible => ({
  type: actionTypes.SET_SIGHT_READING_CURSOR_VISIBLE,
  payload: payload,
});

export const loadUserState = (payload: AppState) => ({
  type: actionTypes.LOAD_USER_STATE,
  payload,
});

export const updateMailchimpData = (payload: Record<string, unknown>) => ({
  type: actionTypes.UPDATE_MAILCHIMP_INFO,
  payload,
});

export const setFullScreen = (payload: boolean) => ({
  type: actionTypes.SET_FULL_SCREEN,
  payload,
});

export const getSubscriptionStatus = () => ({
  type: actionTypes.GET_SUBSCRIPTION_STATUS,
});

export const setSubscriptionStatus = (payload: string) => ({
  type: actionTypes.SET_SUBSCRIPTION_STATUS,
  payload,
});

export const setRepPageSelected = (payload: string) => ({
  type: actionTypes.SET_REP_PAGE_SELECTED,
  payload,
});

export const setErrorRecognitionActive = (payload: ErrorRecognitionActive) => ({
  type: actionTypes.SET_ERROR_RECOGNITION_ACTIVE,
  payload,
});

export const setShowLevelPreview = (payload: boolean) => ({
  type: actionTypes.SET_SHOW_LEVEL_PREVIEW,
  payload,
});

export const userDataRequest = () => ({
  type: actionTypes.USER_DATA_REQUEST,
});
export const setInteractedWithPage = (payload: boolean) => ({
  type: actionTypes.SET_INTERACTED_WITH_PAGE,
  payload,
});

export const invalidateDashboardCache = () => ({
  type: actionTypes.INVALIDATE_DASHBOARD_CACHE,
});

export const setDashboardData = (payload: Record<string, unknown>) => {
  console.log('setDashboardData action called with:', payload);
  const action = {
    type: actionTypes.SET_DASHBOARD_DATA,
    payload,
  };
  console.log('setDashboardData action object:', action);
  return action;
};

export const clearAppData = () => ({
  type: actionTypes.CLEAR_DATA,
});

export const setZoomLevel = (payload: number) => ({
  type: actionTypes.SET_ZOOM_LEVEL,
  payload,
});

export const setAutoTempoAdjustment = (payload: boolean) => ({
  type: actionTypes.SET_AUTO_TEMPO_ADJUSTMENT,
  payload,
});

export const setCustomGoalTempo = (payload: { tempo: number; tier: number }) => ({
  type: actionTypes.SET_CUSTOM_GOAL_TEMPO,
  payload,
});

export const setCustomAtaSettings = (payload: {
  customIncAmt?: number;
  customDecAmt?: number;
  customIncThreshold?: number;
  customDecThreshold?: number;
}) => ({
  type: actionTypes.SET_CUSTOM_ATA_SETTINGS,
  payload,
});

export const setUsingCustomGoalTempo = (payload: boolean) => ({
  type: actionTypes.SET_USING_CUSTOM_GOAL_TEMPO,
  payload,
});

export const setUnseenApptourFeatures = (
  payload: import('Utils/AppTourFeatures').ApptourFeatureItem[],
) => ({
  type: actionTypes.SET_UNSEEN_APPTOUR_FEATURES,
  payload,
});

export const fetchUnseenApptourFeatures = () => ({
  type: actionTypes.FETCH_UNSEEN_APPTOUR_FEATURES,
});

export const markApptourFeaturesAsSeen = (
  payload: import('Utils/AppTourFeatures').ApptourFeature[],
) => ({
  type: actionTypes.MARK_APPTOUR_FEATURES_AS_SEEN,
  payload,
});
